// components/ContactForm.tsx
import { Button } from 'components/ui/button'
import { CustomInput } from 'components/ui/custom-input'
import { CustomTextarea } from 'components/ui/custom-textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog'
import { Field, Form, Formik, FormikHelpers } from 'formik'
import { atom, useAtom } from 'jotai'
import React from 'react'
import { toast } from 'react-hot-toast'
import { ContactFormData } from 'types/contact'
import * as Yup from 'yup'

export const ContactSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, 'Name is too short')
    .max(50, 'Name is too long')
    .required('Name is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  subject: Yup.string()
    .min(2, 'Subject is too short')
    .max(100, 'Subject is too long')
    .required('Subject is required'),
  message: Yup.string()
    .min(10, 'Message is too short')
    .max(1000, 'Message is too long')
    .required('Message is required'),
})

const initialValues: ContactFormData = {
  name: '',
  email: '',
  subject: '',
  message: '',
}

const openDialogAtom = atom(false)
export const useContactFormDialog = () => {
  const [openDialog, setOpenDialog] = useAtom(openDialogAtom)

  return { openDialog, setOpenDialog }
}

const ContactForm: React.FC = () => {
  const { openDialog, setOpenDialog } = useContactFormDialog()

  const handleSubmit = async (
    values: ContactFormData,
    { setSubmitting, resetForm }: FormikHelpers<ContactFormData>
  ): Promise<void> => {
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      toast.success('Message sent successfully!')
      resetForm()
      setOpenDialog(false)
    } catch (error) {
      toast.error('Failed to send message. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={openDialog} onOpenChange={setOpenDialog}>
      <DialogContent className="sm:tw-max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="tw-text-left">Send us a message</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={initialValues}
          validationSchema={ContactSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="tw-space-y-4">
              <div>
                <Field
                  as={CustomInput}
                  name="name"
                  placeholder="Your name"
                  error={touched.name ? errors.name : ''}
                />
                {errors.name && touched.name && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {errors.name}
                  </div>
                )}
              </div>

              <div>
                <Field
                  as={CustomInput}
                  name="email"
                  type="email"
                  placeholder="Your email"
                  error={touched.email ? errors.email : ''}
                />
                {errors.email && touched.email && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {errors.email}
                  </div>
                )}
              </div>

              <div>
                <Field
                  as={CustomInput}
                  name="subject"
                  placeholder="Subject"
                  error={touched.subject ? errors.subject : ''}
                />
                {errors.subject && touched.subject && (
                  <div className="tw-text-red-500 tw-text-sm tw-mt-1">
                    {errors.subject}
                  </div>
                )}
              </div>

              <div>
                <Field
                  as={CustomTextarea}
                  name="message"
                  placeholder="Your message"
                  error={touched.message ? errors.message : ''}
                />
                {errors.message && touched.message && (
                  <div className="tw-text-red-500 tw-text-sm -tw-mt-0.5">
                    {errors.message}
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ContactForm
